/**
 * Язык интерфейса вне React: один источник правды для провайдера (`I18nProvider`) и для
 * `editorText()`, который зовут из обычных функций и не имеет доступа к контексту.
 *
 * Язык посетителя: явный выбор (переключатель, `localStorage`) → язык браузера → русский.
 * Определённый по браузеру язык в `localStorage` НЕ пишем: запись есть только у того, кто
 * выбрал сам, а остальные следуют за настройками браузера.
 *
 * Поверх него — принудительный язык публичной страницы (`setLanguageOverride`): владелец карты
 * закрепил `ru`/`en` в настройках просмотра, и карта, лого и интерфейс не зависят от посетителя.
 */
import type { ViewerMapLanguage } from '@sloy/shared';
import type { Language } from './translations';

export const LANGUAGE_STORAGE_KEY = 'sloy-language';

const DEFAULT_LANGUAGE: Language = 'ru';

function isLanguage(value: unknown): value is Language {
  return value === 'ru' || value === 'en';
}

/**
 * Язык по списку языков браузера (`navigator.languages`, порядок — по предпочтению).
 * Берём первый тег, чья основа — `ru` или `en` (`en-GB` → `en`). Нет ни одного (`de`, `uk`) —
 * русский: продукт русскоязычный, а посетитель с `uk`/`kk` скорее прочтёт его, чем английский.
 */
export function detectBrowserLanguage(languages: readonly string[] | null | undefined): Language {
  for (const tag of languages ?? []) {
    const base = tag.trim().toLowerCase().split(/[-_]/)[0];
    if (isLanguage(base)) {
      return base;
    }
  }
  return DEFAULT_LANGUAGE;
}

/**
 * Явный выбор, который не удалось записать в хранилище (закрыто — встройка с блокировкой куки,
 * приватный режим; переполнено). Живёт до перезагрузки страницы и приоритетнее того, что уже
 * лежит в хранилище: иначе провайдер показывал бы выбранный язык, а `editorText()` и подписи
 * дат читали бы прежний сохранённый или язык браузера. `language: null` — «выбор сброшен», но
 * удалить ключ не удалось: старое значение хранилища учитывать нельзя.
 */
let unsavedChoice: { language: Language | null } | null = null;

/** Явный выбор посетителя: незаписанный (из памяти) → сохранённый → нет выбора. */
export function getStoredLanguage(): Language | null {
  if (unsavedChoice) {
    return unsavedChoice.language;
  }
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Запоминает явный выбор. Закрытое хранилище не повод ронять переключатель: выбор держим в памяти. */
export function storeLanguage(language: Language): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    unsavedChoice = null;
  } catch {
    unsavedChoice = { language };
  }
}

/** Забывает явный выбор: язык снова определяется браузером. Удалить ключ не вышло — помним сброс в памяти. */
export function clearStoredLanguage(): void {
  try {
    localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    unsavedChoice = null;
  } catch {
    unsavedChoice = { language: null };
  }
}

function browserLanguages(): readonly string[] {
  // Вне браузера (node-тесты, у Node 21+ свой `navigator`) язык не определяем: без окна
  // «язык браузера» — выдумка среды, и дефолт обязан быть предсказуемым.
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return [];
  }
  if (navigator.languages?.length) {
    return navigator.languages;
  }
  return navigator.language ? [navigator.language] : [];
}

/** Язык самого посетителя: выбор → браузер → русский. Без учёта языка, закреплённого владельцем карты. */
export function getVisitorLanguage(): Language {
  return getStoredLanguage() ?? detectBrowserLanguage(browserLanguages());
}

/**
 * Язык, закреплённый владельцем публичной карты (`viewerDisplay.mapLanguage` = ru/en).
 * Модульная переменная, а не контекст: `editorText()` зовут из обычных функций, контекста
 * у них нет. Меняет её только `PublicLanguageScope` — в эффекте, то есть для закоммиченной
 * страницы; брошенный рендер её не трогает. Внешнее хранилище (subscribe/get) — чтобы область
 * могла дождаться применения через `useSyncExternalStore`, без setState в эффекте.
 */
let languageOverride: Language | null = null;
const overrideListeners = new Set<() => void>();

export function setLanguageOverride(language: Language | null): void {
  if (languageOverride === language) {
    return;
  }
  languageOverride = language;
  for (const listener of overrideListeners) {
    listener();
  }
}

export function getLanguageOverride(): Language | null {
  return languageOverride;
}

export function subscribeLanguageOverride(listener: () => void): () => void {
  overrideListeners.add(listener);
  return () => {
    overrideListeners.delete(listener);
  };
}

/** Текущий язык интерфейса: закреплённый владельцем карты → выбор посетителя → браузер → русский. */
export function getCurrentLanguage(): Language {
  return languageOverride ?? getVisitorLanguage();
}

/**
 * Какой язык закрепить на публичной странице: `ru`/`en` из «Настроек просмотра» — закрепляем,
 * `auto` и «не задано» — оставляем язык посетителя (`null`). Лежит здесь, а не рядом с
 * `PublicLanguageScope`: файл компонента должен экспортировать только компоненты (Fast Refresh).
 *
 * @param enabled false — вьювер внутри приложения (предпросмотр редактора, просмотр участником):
 *   там язык свой, и закрепление не должно менять интерфейс редактора за спиной.
 */
export function resolveForcedLanguage(
  mapLanguage: ViewerMapLanguage | undefined,
  enabled = true,
): Language | null {
  return enabled && (mapLanguage === 'ru' || mapLanguage === 'en') ? mapLanguage : null;
}
